// The shapes everything else agrees on. Amounts are always integer cents.
import type { GroupShareArm, SplitFee } from './experiments.ts';
import type { PayerSource } from './attribution.ts';
export type ReminderStep = 'd21' | 'd45';

export type Cents = number;

export interface Item {
  id: string;
  name: string;
  cents: Cents;
}

/** One person's claim on an item. `by` is who put them on it (themselves, or someone who marked them). */
export interface Claim {
  pid: string;
  by: string;
  /** Set when the person saw a mark someone else made and chose to keep it. */
  ack?: boolean;
}

export interface Paid {
  cents: Cents;
  at: string;
  how: 'self' | 'app' | 'other' | 'payer' | string;
}

export interface Paying {
  app: 'venmo' | 'cashapp';
  at: string;
}

export interface Person {
  id: string;
  /** Secret that proves "this phone is this person". Never leaves the server. */
  token: string;
  name: string;
  isPayer?: boolean;
  /**
   * The friend's own number (given at join or on the done screen). Used only by our server: their
   * receipt and reminder texts, and their referral code. Never sent to the host.
   */
  phone?: string;
  /** A number the host typed for this person (to text a reminder from their own phone). The host's own note. */
  hostPhone?: string;
  joinedAt: string;
  paid: Paid | null;
  forgiven?: boolean;
  addedByPayer?: boolean;
  /**
   * Tapped Pay with Venmo or Cash App and left for that app. Not the same as paid: it only means
   * they're on their way. Cleared once they (or the payer) mark them paid.
   */
  paying?: Paying | null;
  /** When a phone first held this spot. Payer-added spots never get one: no phone can claim them. */
  deviceAt?: string;
  /**
   * Said yes to offers from this restaurant by text. Kept apart from `phone`, which is only for the
   * payer's reminders: the payer never sees this number. `wording` is exactly what they agreed to.
   */
  smsOptIn?: { phone: string; at: string; venue: string; wording: string };
  /**
   * Texts from our own app (src/sms.ts). Giving a number means one receipt text when they tap
   * Pay. `reminder` is the separate, unticked-by-default box for the later free-split text.
   */
  sms?: { at: string; wording: string; reminder: boolean; origin?: string; receiptAt?: string; reminderDue?: string; reminderAt?: string; stopped?: boolean;
    /** The friend's IANA time zone from their phone, so reminders land Thu/Fri early evening for them. */
    tz?: string;
    /** The two free-split reminders: when each is due and when it went out (or was skipped). */
    reminders?: Partial<Record<ReminderStep, { due: string; at?: string }>> };
  /** The optional three-tap survey after the bill is settled. Shown to the restaurant only as totals. */
  survey?: Survey;
}

export const OCCASIONS = ['Birthday', 'Date', 'Friends', 'Work', 'Family', 'Just because'] as const;
export const COME_BACK = ['A deal', 'Happy hour', 'Easier reservations', 'A new menu', 'Room for big groups', 'Nothing, it was great'] as const;
export interface Survey {
  /** 1 to 5. */
  rating?: number;
  occasion?: (typeof OCCASIONS)[number];
  comeBack?: (typeof COME_BACK)[number];
  at: string;
}

export interface Payment {
  name: string;
  venmo: string;
  cashapp: string;
  zelle: string;
}

export type LeftoverMode = 'even' | 'cover' | 'assign';
export interface Leftover {
  mode: LeftoverMode;
  assignTo?: string;
}

export type SplitStatus = 'open' | 'closed';

export interface Split {
  code: string;
  /** Secret that unlocks the payer's controls. Never leaves the server except to the payer. */
  adminKey: string;
  /** Random value set when the split is made; picks A/B arms so they don't drift with the code's restaurant prefix. Older splits fall back to the code. */
  abSeed?: string;
  venue: string;
  date: string;
  status: SplitStatus;
  createdAt: string;
  updatedAt?: string;
  closedAt?: string;
  version?: number;
  taxCents: Cents;
  /** "Tax & fees" when receipt fees are folded into the tax. Empty means plain tax. */
  taxLabel?: string;
  /** Fees included in taxCents, by name, so people can see what they are. */
  fees?: Array<{ name: string; cents: Cents }>;
  tipCents: Cents;
  tipLabel: string;
  /** Where the tip came from: the receipt, the host's pick, or "No tip". Missing on older splits. */
  tipSource?: 'receipt' | 'host_pick' | 'no_tip';
  thanksOn: boolean;
  thanksCents: Cents;
  /** People at the table, counting the payer. 0 when the payer didn't say. */
  partySize: number;
  /** Split evenly: everyone at the table pays the same share of the whole bill. Nobody taps items. */
  even?: boolean;
  payment: Payment;
  items: Item[];
  claims: Record<string, Claim[]>;
  leftover: Leftover;
  people: Person[];
  /** Where the restaurant is, from the receipt. Missing for typed-in splits and older ones. */
  place?: Place;
  /** The promo, referral or campaign code this split came with, if any. */
  refCode?: string;
  /** The payer's own referral code, made on their first split (any split with a payment handle). Friends' "Start a split" link carries it. */
  payerRef?: string;
  /** The table loop: the split whose "Start a split" link brought this one, and that split's payer (hashed). */
  from?: { code: string; payerKey: string };
  /** The owner marked this as a test run: it still works, but it's left out of every number. */
  test?: boolean;
  /** The app fee and its A/B group, set when the split is made while the fee test is on. */
  fee?: SplitFee;
  /** The signed-in payer whose saved card pays the fee. */
  payerAccount?: string;
  /** The host's phone key (phoneKey in src/codes.ts) when they were signed in with a verified phone. */
  hostPhoneKey?: string;
  /**
   * The host phone's random device id (never a name or number), when it sent one. Ties a host's
   * splits together across handles, for self-referral and "is this host new?". Never sent to phones.
   */
  hostDevice?: string;
  /** Set when this split's fee was waived by a free split banked on that phone (a friend's code credit). */
  phoneCreditUsed?: string;
  /**
   * Made or used while pricing was off (PRICING_ENABLED, src/pricing.ts). Never charged, and it
   * doesn't use up a referred first split, a code's perk or a credit for when pricing comes back.
   */
  pricingOff?: boolean;
  /**
   * The host turned the $1 thanks on themselves (the tip-step switch, or the board). While pricing
   * is off, a thanks the host didn't pick is turned off on unsent splits, so friends pay only their share.
   */
  thanksPicked?: boolean;
  /**
   * The fee charge. "waived": no fee for this split (a referred host's first, a code's perk, a
   * credit) or no fee for this group. "pending": owed, and paid by the host when they lock the split in.
   */
  charge?: FeeCharge;
  /**
   * False until the host locks the split in: friends tap items but don't see their total or a pay
   * button. Splits made before lock-in existed have no field and count as locked.
   */
  locked?: boolean;
  lockedAt?: string;
  /** Before lock-in: when each friend (by person id) last tapped "Ask <Host> to lock it in". */
  nudges?: Record<string, string>;
  /**
   * When the split was first used: a friend picked an item, or the host locked it in. Only then does
   * it use up a free split (or owe the fee). A split nobody used never counts.
   */
  usedAt?: string;
  /**
   * Where this payer came from. Only on a payer's first split (by payment handle); see
   * src/attribution.ts. `acquiredAt` is set once, when every friend has paid back.
   */
  /** The group-text button test's arm for this table. Missing on older splits: they had QR first. */
  groupShare?: GroupShareArm;
  source?: { source: PayerSource; detail: string; firstSeen: string; acquiredAt?: string };
}

export interface FeeCharge {
  status: 'waived' | 'pending' | 'charging' | 'succeeded' | 'failed' | 'refunded' | 'disputed';
  cents: Cents;
  /** Stripe's PaymentIntent id. */
  id?: string;
  /** Why it was waived: "free" (a referred first split, a code perk, or a credit), "cards_off" (no card service set up), "no_fee" (group B) or "pricing_off" (pricing switched off). */
  reason?: string;
  /** Payment attempts at lock-in, so each gets its own idempotency key. */
  tries?: number;
  at?: string;
}

/** What anyone with the link may see: no tokens, no admin key, no phone numbers. */
export interface PublicPerson {
  id: string;
  name: string;
  isPayer: boolean;
  joinedAt: string;
  paid: Paid | null;
  forgiven: boolean;
  addedByPayer: boolean;
  /** On their way to pay in Venmo or Cash App (not confirmed). */
  paying: Paying | null;
}

export interface PublicSplit {
  code: string;
  venue: string;
  date: string;
  status: SplitStatus;
  createdAt: string;
  version: number;
  thanksOn: boolean;
  thanksCents: Cents;
  taxCents: Cents;
  taxLabel: string;
  fees: Array<{ name: string; cents: Cents }>;
  tipCents: Cents;
  tipLabel: string;
  tipSource?: 'receipt' | 'host_pick' | 'no_tip';
  partySize: number;
  even: boolean;
  payer: Payment;
  items: Item[];
  claims: Record<string, Claim[]>;
  leftover: Leftover;
  people: PublicPerson[];
  isAdmin?: boolean;
  /** The payer's referral code, for the friend's "Start a split" link. */
  payerRef?: string;
  /** Payer only: splits with no fee earned (a referred first split, a code perk, referral rewards) and not used yet. */
  freeBanked?: number;
  /** Free splits banked on a phone this device gave as a friend, waiting for the host to sign in with it. */
  phoneCreditsWaiting?: number;
  /**
   * Payer only: this split owes the fee only because the host's older open split, not used yet, is
   * the one shown with no fee. The board says so under the button.
   */
  waiverOnOther?: boolean;
  /** Payer only: phone numbers by person id. */
  phones?: Record<string, string>;
  /** The app fee and its A/B group, when there is one. */
  fee?: SplitFee;
  groupShare?: GroupShareArm;
  /** False until the host locks it in (friends see totals and pay buttons only after). Missing means locked. */
  locked?: boolean;
  /** Payer only, before lock-in: what locking costs. `free`: a free split or credit covers it; `nextCents` is the price after. */
  lock?: { cents: Cents; free: boolean; nextCents?: Cents };
  /** Payer only, before lock-in: friends who asked the host to lock it in, latest last. */
  nudges?: Array<{ pid: string; name: string; at: string }>;
}

/** The subset of a split the money math needs. Both Split and PublicSplit satisfy it. */
export interface SplitForMath {
  status: SplitStatus;
  taxCents: Cents;
  tipCents: Cents;
  thanksOn: boolean;
  thanksCents?: Cents;
  items: Item[];
  claims: Record<string, Claim[]>;
  leftover?: Leftover;
  /** Split evenly across `partySize` seats (or everyone on the split, if more joined). */
  even?: boolean;
  partySize?: number;
  /** Group C: friends share the app fee, on top of their share. */
  fee?: SplitFee;
  people: Array<{ id: string; name: string; isPayer?: boolean; paid: Paid | null; forgiven?: boolean }>;
}

/** Where a restaurant is, read off the receipt. Used to tell apart locations of one chain. */
export interface Place {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  /** Digits only. */
  phone: string;
  storeNumber: string;
}

export interface ParsedReceipt {
  venue: string;
  date: string;
  place: Place | null;
  /** maybeFee: the name looks like it could be a fee, so the payer is asked. */
  items: Array<{ name: string; cents: Cents; fromQuantity?: number; maybeFee?: boolean }>;
  subtotalCents: Cents | null;
  taxCents: Cents | null;
  serviceChargeCents: Cents | null;
  /** Card surcharges, processing or delivery fees: split like tax. */
  feesCents: Cents | null;
  /** The same fees one by one, with their printed names. */
  fees: Array<{ name: string; cents: Cents }>;
  tipCents: Cents | null;
  totalCents: Cents | null;
  guests: number | null;
}
